import { Accordion, Box, Flex, Link, useColorModeValue } from '@chakra-ui/react';
import _range from 'lodash/range';
import React from 'react';

import type { SmartContractMethod } from './types';

import { route } from 'nextjs-routes';

import { useI18n } from 'lib/i18n/context';
import LinkInternal from 'ui/shared/links/LinkInternal';

import { ADDRESS_CONTRACT_KEYS } from '../../i18n';
import ContractAbiItem from './ContractAbiItem';
import useFormSubmit from './useFormSubmit';
import useScrollToMethod from './useScrollToMethod';

interface Props {
  abi: Array<SmartContractMethod>;
  visibleItems?: Array<number>;
  addressHash: string;
  tab: string;
  sourceAddress?: string;
  filters?: React.ReactNode;
}

const ContractAbi = ({ abi, addressHash, sourceAddress, tab, visibleItems, filters }: Props) => {
  const { t } = useI18n();
  const [ expandedSections, setExpandedSections ] = React.useState<Array<number>>(abi.length === 1 ? [ 0 ] : []);
  const [ id, setId ] = React.useState(0);
  const panelBg = useColorModeValue('#FFFFFF', '#111111');
  const panelBorder = useColorModeValue('#E9ECEF', '#222222');
  const headingColor = useColorModeValue('#000000', '#FFFFFF');
  const actionColor = useColorModeValue('#14DCAC', '#14DCAC');

  useScrollToMethod(abi, setExpandedSections);

  const handleFormSubmit = useFormSubmit({ addressHash });

  const handleAccordionStateChange = React.useCallback((newValue: Array<number>) => {
    setExpandedSections(newValue);
  }, []);

  const handleExpandAll = React.useCallback(() => {
    if (!abi) {
      return;
    }

    if (expandedSections.length < abi.length) {
      setExpandedSections(_range(0, abi.length));
    } else {
      setExpandedSections([]);
    }
  }, [ abi, expandedSections.length ]);

  const handleReset = React.useCallback(() => {
    setId((id) => id + 1);
  }, []);

  const hasVisibleItems = !visibleItems || visibleItems.length > 0;

  return (
    <Box
      bg={ panelBg }
      border="1px solid"
      borderColor={ panelBorder }
      borderRadius="8px"
      p={ 4 }
    >
      <Flex flexDir="column" gap={ 4 }>
        { filters }
        <Flex alignItems="center" flexWrap="wrap" rowGap={ 2 } columnGap={ 2 }>
          <Box fontWeight={ 700 } fontSize="14px" color={ headingColor } mr="auto">
            { t(ADDRESS_CONTRACT_KEYS.labels.contractInformation) }
          </Box>
          <Flex alignItems="center" gap={ 2 } fontSize="14px" fontWeight={ 500 }>
            { abi.length > 1 && (
              <Link onClick={ handleExpandAll } color={ actionColor } _hover={{ textDecoration: 'none', opacity: 0.9 }}>
                { expandedSections.length === abi.length ? t(ADDRESS_CONTRACT_KEYS.actions.collapse) : t(ADDRESS_CONTRACT_KEYS.actions.expand) }
              </Link>
            ) }
            <Link onClick={ handleReset } color={ actionColor } _hover={{ textDecoration: 'none', opacity: 0.9 }}>
              { t(ADDRESS_CONTRACT_KEYS.actions.reset) }
            </Link>
          </Flex>
        </Flex>
        <Accordion allowMultiple position="relative" onChange={ handleAccordionStateChange } index={ expandedSections }>
          { abi.map((item, index) => (
            <ContractAbiItem
              key={ index }
              id={ id }
              index={ index }
              data={ item }
              isVisible={ !visibleItems || visibleItems.includes(index) }
              addressHash={ addressHash }
              sourceAddress={ sourceAddress }
              tab={ tab }
              onSubmit={ handleFormSubmit }
            />
          )) }
        </Accordion>
        { !hasVisibleItems && (
          <div>
            <div>{ t(ADDRESS_CONTRACT_KEYS.notice.noMethodMatches) }</div>
            <div>
              { t(ADDRESS_CONTRACT_KEYS.notice.customAbiHintPrefix) }{ ' ' }
              <LinkInternal
                href={ route({ pathname: '/address/[hash]', query: { hash: addressHash, tab: 'read_write_custom_methods' } }) }
                scroll={ false }
              >
                { t(ADDRESS_CONTRACT_KEYS.tabs.customAbi) }
              </LinkInternal>
              { ' ' }{ t(ADDRESS_CONTRACT_KEYS.notice.customAbiHintSuffix) }
            </div>
          </div>
        ) }
      </Flex>
    </Box>
  );
};

export default React.memo(ContractAbi);
